import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  Shield,
  Clock,
  Package,
  Wrench,
  FileText,
  CheckCircle2,
  ExternalLink,
  Eye,
  CheckCheck,
  Loader2,
  TrendingUp,
  Calendar,
  MapPin,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, parseISO, differenceInDays } from 'date-fns';
import type { AssetAlert, AlertType, AlertSeverity } from '@/types/alerts';
import { getAlertsForAsset, generateAlertsForAsset } from '@/services/alerts-service';
import type { Asset } from '@/types/asset';
import { useNavigate } from 'react-router-dom';

interface AssetAlertsTabProps {
  assetId: string;
  assetName: string;
  asset?: Asset;
  disabled?: boolean;
  onAlertsChange?: (alertCount: number) => void;
}

const alertTypeConfig = {
  compliance: {
    label: 'Compliance',
    icon: Shield,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-950/20',
    borderColor: 'border-red-200 dark:border-red-800',
    lightBg: 'bg-red-50/50 dark:bg-red-950/10',
  },
  checkinout: {
    label: 'Check-In/Out',
    icon: Clock,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-950/20',
    borderColor: 'border-orange-200 dark:border-orange-800',
    lightBg: 'bg-orange-50/50 dark:bg-orange-950/10',
  },
  maintenance: {
    label: 'Maintenance',
    icon: Wrench,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-950/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
    lightBg: 'bg-blue-50/50 dark:bg-blue-950/10',
  },
  insurance: {
    label: 'Insurance',
    icon: Shield,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-950/20',
    borderColor: 'border-purple-200 dark:border-purple-800',
    lightBg: 'bg-purple-50/50 dark:bg-purple-950/10',
  },
  warranty: {
    label: 'Warranty',
    icon: FileText,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-950/20',
    borderColor: 'border-green-200 dark:border-green-800',
    lightBg: 'bg-green-50/50 dark:bg-green-950/10',
  },
};

const severityConfig = {
  critical: {
    label: 'Critical',
    icon: AlertTriangle,
    color: 'text-red-600 dark:text-red-400',
    badgeVariant: 'destructive' as const,
  },
  warning: {
    label: 'Warning',
    icon: AlertCircle,
    color: 'text-yellow-600 dark:text-yellow-400',
    badgeVariant: 'default' as const,
  },
  info: {
    label: 'Info',
    icon: Info,
    color: 'text-blue-600 dark:text-blue-400',
    badgeVariant: 'secondary' as const,
  },
};

export function AssetAlertsTab({ assetId, assetName, asset, disabled = false, onAlertsChange }: AssetAlertsTabProps) {
  const [alerts, setAlerts] = useState<AssetAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState<AssetAlert | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'acknowledge' | 'resolve'>('acknowledge');
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | AlertType>('all');
  const navigate = useNavigate();

  useEffect(() => {
    loadAlerts();
  }, [assetId]);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      let fetchedAlerts = await getAlertsForAsset(assetId);
      
      // If no alerts from API and we have asset data, generate some from asset properties
      if (fetchedAlerts.length === 0 && asset) {
        fetchedAlerts = generateAlertsForAsset(asset);
      }
      
      setAlerts(fetchedAlerts);
      
      // Notify parent of alert count
      const activeAlerts = fetchedAlerts.filter(a => a.status === 'active');
      if (onAlertsChange) {
        onAlertsChange(activeAlerts.length);
      }
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (alert: AssetAlert) => {
    setSelectedAlert(alert);
    setDetailDialogOpen(true);
  };

  const handleNavigateToCompliance = (alert?: AssetAlert) => {
    if (alert?.complianceData?.assignmentId) {
      // Navigate to compliance page with assignment highlighted
      navigate(`/compliance-old?assetId=${assetId}&assignmentId=${alert.complianceData.assignmentId}`);
    } else {
      // Navigate to general compliance page filtered by this asset
      navigate(`/compliance-old?assetId=${assetId}`);
    }
  };

  const handleNavigateToCheckInOut = (alert: AssetAlert) => {
    if (alert.checkInOutData?.checkOutRecordId) {
      navigate(`/checkinout?recordId=${alert.checkInOutData.checkOutRecordId}`);
    } else {
      navigate('/checkinout');
    }
  };

  const handleAcknowledge = (alert: AssetAlert) => {
    setSelectedAlert(alert);
    setActionType('acknowledge');
    setActionDialogOpen(true);
  };

  const handleResolve = (alert: AssetAlert) => {
    setSelectedAlert(alert);
    setActionType('resolve');
    setActionDialogOpen(true);
  };

  const handleActionSubmit = async () => {
    if (!selectedAlert) return;
    
    setActionLoading(true);
    try {
      // TODO: Implement API call to acknowledge/resolve alert
      console.log(`${actionType} alert:`, selectedAlert.id, actionNotes);
      
      // Reload alerts after action
      await loadAlerts();
      
      // Close dialogs
      setActionDialogOpen(false);
      setDetailDialogOpen(false);
      setActionNotes('');
    } catch (error) {
      console.error(`Failed to ${actionType} alert:`, error);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredAlerts = activeTab === 'all'
    ? alerts 
    : alerts.filter(a => a.type === activeTab);

  const activeAlerts = filteredAlerts.filter(a => a.status === 'active');
  const acknowledgedAlerts = filteredAlerts.filter(a => a.status === 'acknowledged');
  const resolvedAlerts = filteredAlerts.filter(a => a.status === 'resolved');

  // Calculate alert counts by type
  const alertCounts = {
    all: alerts.filter(a => a.status === 'active').length,
    compliance: alerts.filter(a => a.type === 'compliance' && a.status === 'active').length,
    checkinout: alerts.filter(a => a.type === 'checkinout' && a.status === 'active').length,
    maintenance: alerts.filter(a => a.type === 'maintenance' && a.status === 'active').length,
    insurance: alerts.filter(a => a.type === 'insurance' && a.status === 'active').length,
    warranty: alerts.filter(a => a.type === 'warranty' && a.status === 'active').length,
  };

  // Calculate summary statistics
  const criticalCount = activeAlerts.filter(a => a.severity === 'critical').length;
  const warningCount = activeAlerts.filter(a => a.severity === 'warning').length;
  const overdueCount = activeAlerts.filter(a => a.daysOverdue && a.daysOverdue > 0).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="relative">
        {!disabled && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 bg-primary/90 text-primary-foreground px-3 py-1 text-xs font-medium rounded-full shadow-sm">
            Editing
          </div>
        )}
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
          <CardTitle>Asset Alerts</CardTitle>
          <CardDescription>
                View all alerts and notifications for this asset
          </CardDescription>
            </div>
            <Button onClick={() => handleNavigateToCompliance()}>
              <ExternalLink className="mr-2 h-4 w-4" />
              View in Compliance
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Alert Type Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="space-y-4">
            <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6">
              <TabsTrigger 
                value="all" 
                badge={alertCounts.all > 0 ? alertCounts.all : undefined}
                badgeVariant="destructive"
              >
                All
                {alertCounts.all > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 min-w-5 px-1">
                    {alertCounts.all}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="compliance" 
                badge={alertCounts.compliance > 0 ? alertCounts.compliance : undefined}
                badgeVariant="destructive"
              >
                Compliance
                {alertCounts.compliance > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 min-w-5 px-1">
                    {alertCounts.compliance}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="checkinout" 
                badge={alertCounts.checkinout > 0 ? alertCounts.checkinout : undefined}
                badgeVariant="destructive"
              >
                Check-In/Out
                {alertCounts.checkinout > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 min-w-5 px-1">
                    {alertCounts.checkinout}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="maintenance" 
                badge={alertCounts.maintenance > 0 ? alertCounts.maintenance : undefined}
                badgeVariant="destructive"
              >
                Maintenance
                {alertCounts.maintenance > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 min-w-5 px-1">
                    {alertCounts.maintenance}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="insurance" 
                badge={alertCounts.insurance > 0 ? alertCounts.insurance : undefined}
                badgeVariant="destructive"
              >
                Insurance
                {alertCounts.insurance > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 min-w-5 px-1">
                    {alertCounts.insurance}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="warranty" 
                badge={alertCounts.warranty > 0 ? alertCounts.warranty : undefined}
                badgeVariant="destructive"
              >
                Warranty
                {alertCounts.warranty > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 min-w-5 px-1">
                    {alertCounts.warranty}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="space-y-4">
              {/* Active Alerts */}
              {activeAlerts.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-foreground">Active Alerts ({activeAlerts.length})</h3>
                    {activeTab === 'compliance' && activeAlerts.length > 0 && (
                      <Button size="sm" variant="outline" onClick={() => handleNavigateToCompliance()}>
                        <ExternalLink className="mr-2 h-3 w-3" />
                        View All in Compliance
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                  {activeAlerts.map((alert) => (
                      <CompactAlertCard
                      key={alert.id}
                      alert={alert}
                      onViewDetails={handleViewDetails}
                      onAcknowledge={handleAcknowledge}
                      onResolve={handleResolve}
                      onNavigateToCompliance={handleNavigateToCompliance}
                      onNavigateToCheckInOut={handleNavigateToCheckInOut}
                    />
                  ))}
                </div>
                </div>
              )}

              {/* Acknowledged Alerts */}
              {acknowledgedAlerts.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground">Acknowledged ({acknowledgedAlerts.length})</h3>
                  <div className="space-y-2">
                  {acknowledgedAlerts.map((alert) => (
                      <CompactAlertCard
                      key={alert.id}
                      alert={alert}
                      onViewDetails={handleViewDetails}
                      onResolve={handleResolve}
                      onNavigateToCompliance={handleNavigateToCompliance}
                      onNavigateToCheckInOut={handleNavigateToCheckInOut}
                    />
                  ))}
                </div>
                </div>
              )}

              {/* Resolved Alerts */}
              {resolvedAlerts.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground">Resolved ({resolvedAlerts.length})</h3>
                  <div className="space-y-2">
                  {resolvedAlerts.map((alert) => (
                      <CompactAlertCard
                      key={alert.id}
                      alert={alert}
                      onViewDetails={handleViewDetails}
                      onNavigateToCompliance={handleNavigateToCompliance}
                      onNavigateToCheckInOut={handleNavigateToCheckInOut}
                    />
                  ))}
                </div>
                </div>
              )}

              {/* No Alerts */}
              {filteredAlerts.length === 0 && (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>
                    No {activeTab !== 'all' ? alertTypeConfig[activeTab as AlertType].label.toLowerCase() : ''} alerts for this asset.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Alert Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Alert Details</DialogTitle>
          </DialogHeader>
          {selectedAlert && <AlertDetailView alert={selectedAlert} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setDetailDialogOpen(false);
              if (selectedAlert?.type === 'compliance') {
                handleNavigateToCompliance(selectedAlert);
              } else if (selectedAlert?.type === 'checkinout') {
                handleNavigateToCheckInOut(selectedAlert);
              } else {
                handleNavigateToCompliance();
              }
            }}>
              <ExternalLink className="mr-2 h-4 w-4" />
              View in Compliance
            </Button>
            <Button 
              onClick={handleActionSubmit} 
              disabled={actionLoading || (actionType === 'resolve' && !actionNotes.trim())}
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {actionType === 'acknowledge' ? 'Acknowledge' : 'Resolve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface CompactAlertCardProps {
  alert: AssetAlert;
  onViewDetails: (alert: AssetAlert) => void;
  onAcknowledge?: (alert: AssetAlert) => void;
  onResolve?: (alert: AssetAlert) => void;
  onNavigateToCompliance?: (alert?: AssetAlert) => void;
  onNavigateToCheckInOut?: (alert: AssetAlert) => void;
}

function CompactAlertCard({ 
  alert, 
  onViewDetails, 
  onAcknowledge, 
  onResolve,
  onNavigateToCompliance,
  onNavigateToCheckInOut,
}: CompactAlertCardProps) {
  const typeConfig = alertTypeConfig[alert.type];
  const severityConfig_ = severityConfig[alert.severity];
  const TypeIcon = typeConfig.icon;
  const SeverityIcon = severityConfig_.icon;

  const handleNavigate = () => {
    if (alert.type === 'compliance' && onNavigateToCompliance) {
      onNavigateToCompliance(alert);
    } else if (alert.type === 'checkinout' && onNavigateToCheckInOut) {
      onNavigateToCheckInOut(alert);
    }
  };

  // Determine background color based on severity (subtle colors)
  const getSeverityBg = () => {
    if (alert.severity === 'critical') {
      return 'bg-red-50/50 dark:bg-red-950/10 border-l-red-500';
    } else if (alert.severity === 'warning') {
      return 'bg-amber-50/50 dark:bg-amber-950/10 border-l-amber-500';
    }
    return 'bg-green-50/50 dark:bg-green-950/10 border-l-green-500';
  };

  // Icon color and background based on severity
  const getSeverityIconStyle = () => {
    if (alert.severity === 'critical') {
      return {
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        iconColor: 'text-red-600 dark:text-red-400',
      };
    } else if (alert.severity === 'warning') {
      return {
        bgColor: 'bg-amber-100 dark:bg-amber-900/30',
        iconColor: 'text-amber-600 dark:text-amber-400',
      };
    }
    return {
      bgColor: 'bg-green-100 dark:bg-green-900/30',
      iconColor: 'text-green-600 dark:text-green-400',
    };
  };

  // Badge styling based on severity
  const getSeverityBadgeClass = () => {
    if (alert.severity === 'critical') {
      return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
    } else if (alert.severity === 'warning') {
      return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
    }
    return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
  };

  const iconStyle = getSeverityIconStyle();

  return (
    <div className={cn(
      'group relative rounded-lg border-l-4 border p-3 transition-all hover:shadow-md',
      getSeverityBg()
    )}>
      <div className="flex items-center gap-3">
        {/* Icon - severity-based color */}
        <div className={cn('rounded-lg p-1.5 shrink-0', iconStyle.bgColor)}>
          <TypeIcon className={cn('h-4 w-4', iconStyle.iconColor)} />
          </div>
          
        {/* Main Content - Title and Description */}
        <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
            <h4 className="font-semibold text-sm truncate">{alert.title}</h4>
            <Badge className={cn('shrink-0 text-xs border', getSeverityBadgeClass())}>
                    {severityConfig_.label}
                  </Badge>
                  <Badge variant="outline">{typeConfig.label}</Badge>
                  {alert.status !== 'active' && (
                    <Badge variant="secondary">{alert.status}</Badge>
                  )}
                </div>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{alert.description}</p>
        </div>

        {/* Right Side - Details and Actions */}
        <div className="flex items-center gap-3 shrink-0">
          {/* Metadata */}
          <div className="flex flex-col items-end gap-1 text-xs text-muted-foreground">
              {alert.dueDate && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {format(parseISO(alert.dueDate), 'dd MMM yyyy')}
              </span>
              )}
              {alert.daysOverdue && alert.daysOverdue > 0 && (
              <span className="flex items-center gap-1 text-red-600 dark:text-red-400 font-medium">
                <AlertTriangle className="h-3 w-3" />
                  {alert.daysOverdue} days overdue
                </span>
              )}
            {alert.complianceData && !alert.dueDate && (
              <span className="flex items-center gap-1">
                <Shield className="h-3 w-3" />
                {alert.complianceData.regimeName}
              </span>
            )}
            </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => onViewDetails(alert)}>
              <Eye className="h-4 w-4" />
              </Button>
              
              {alert.canNavigate && (alert.type === 'compliance' || alert.type === 'checkinout') && (
              <Button size="sm" variant="default" className="h-8" onClick={handleNavigate}>
                <ExternalLink className="mr-1.5 h-3 w-3" />
                View
                </Button>
              )}
            </div>
          </div>
        </div>
    </div>
  );
}

function AlertDetailView({ alert }: { alert: AssetAlert }) {
  const typeConfig = alertTypeConfig[alert.type];
  const severityConfig_ = severityConfig[alert.severity];
  const TypeIcon = typeConfig.icon;
  const SeverityIcon = severityConfig_.icon;

  // Icon color and background based on severity
  const getSeverityIconStyle = () => {
    if (alert.severity === 'critical') {
      return {
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        iconColor: 'text-red-600 dark:text-red-400',
      };
    } else if (alert.severity === 'warning') {
      return {
        bgColor: 'bg-amber-100 dark:bg-amber-900/30',
        iconColor: 'text-amber-600 dark:text-amber-400',
      };
    }
    return {
      bgColor: 'bg-green-100 dark:bg-green-900/30',
      iconColor: 'text-green-600 dark:text-green-400',
    };
  };

  // Badge style based on severity
  const getSeverityBadgeStyle = () => {
    if (alert.severity === 'critical') {
      return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
    } else if (alert.severity === 'warning') {
      return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
    }
    return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
  };

  const iconStyle = getSeverityIconStyle();

  return (
    <ScrollArea className="max-h-[60vh]">
      <div className="space-y-4 pr-4">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className={cn('rounded-lg p-3', iconStyle.bgColor)}>
            <TypeIcon className={cn('h-6 w-6', iconStyle.iconColor)} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{alert.title}</h3>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={cn('border', getSeverityBadgeStyle())}>
                <SeverityIcon className="mr-1 h-3 w-3" />
                {severityConfig_.label}
              </Badge>
              <Badge variant="outline">{typeConfig.label}</Badge>
              {alert.status !== 'active' && (
                <Badge variant="secondary">{alert.status}</Badge>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Description</h4>
          <p className="text-sm">{alert.description}</p>
        </div>

        {/* Timeline Information */}
        <div className="grid gap-3 sm:grid-cols-2">
        {alert.dueDate && (
            <div className="space-y-1">
              <h4 className="text-sm font-medium text-muted-foreground">Due Date</h4>
              <p className="text-sm font-medium">{format(parseISO(alert.dueDate), 'dd MMMM yyyy')}</p>
          </div>
        )}

        {alert.daysOverdue && alert.daysOverdue > 0 && (
            <div className="space-y-1">
              <h4 className="text-sm font-medium text-muted-foreground">Days Overdue</h4>
            <p className="text-sm font-medium text-red-600 dark:text-red-400">
              {alert.daysOverdue} days
            </p>
          </div>
        )}

          <div className="space-y-1">
            <h4 className="text-sm font-medium text-muted-foreground">Created</h4>
            <p className="text-sm">{format(parseISO(alert.createdDate), 'dd MMM yyyy HH:mm')}</p>
          </div>
        </div>

        {/* Type-specific details */}
        {alert.complianceData && (
          <div className="space-y-3 rounded-lg border p-4 bg-muted/50">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Compliance Details
            </h4>
            <div className="grid gap-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Regime:</span>
                <span className="col-span-2 font-medium">{alert.complianceData.regimeName}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Requirement:</span>
                <span className="col-span-2 font-medium">{alert.complianceData.requirementName}</span>
              </div>
              {alert.complianceData.lastEventDate && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Last Event:</span>
                  <span className="col-span-2">{format(parseISO(alert.complianceData.lastEventDate), 'dd MMM yyyy')}</span>
                </div>
              )}
              {alert.complianceData.nextDueDate && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Next Due:</span>
                  <span className="col-span-2">{format(parseISO(alert.complianceData.nextDueDate), 'dd MMM yyyy')}</span>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Status:</span>
                <span className="col-span-2">
                  <Badge variant={
                    alert.complianceData.complianceStatus === 'compliant' ? 'secondary' :
                    alert.complianceData.complianceStatus === 'overdue' ? 'destructive' : 'default'
                  }>
                    {alert.complianceData.complianceStatus}
                  </Badge>
                </span>
              </div>
            </div>
          </div>
        )}

        {alert.checkInOutData && (
          <div className="space-y-3 rounded-lg border p-4 bg-muted/50">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Check-In/Out Details
            </h4>
            <div className="grid gap-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Assigned To:</span>
                <span className="col-span-2 font-medium">{alert.checkInOutData.assignedToName}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Checked Out:</span>
                <span className="col-span-2">{format(parseISO(alert.checkInOutData.checkOutDate), 'dd MMM yyyy')}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Expected Return:</span>
                <span className="col-span-2">{format(parseISO(alert.checkInOutData.expectedReturnDate), 'dd MMM yyyy')}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Current Location:</span>
                <span className="col-span-2 font-medium">{alert.checkInOutData.currentLocation}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Status:</span>
                <span className="col-span-2">
                  <Badge variant={
                    alert.checkInOutData.checkOutStatus === 'overdue' ? 'destructive' :
                    alert.checkInOutData.checkOutStatus === 'due-soon' ? 'default' : 'secondary'
                  }>
                    {alert.checkInOutData.checkOutStatus}
                  </Badge>
                </span>
              </div>
            </div>
          </div>
        )}

        {alert.insuranceData && (
          <div className="space-y-3 rounded-lg border p-4 bg-muted/50">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Insurance Details
            </h4>
            <div className="grid gap-3 text-sm">
              {alert.insuranceData.policyNumber && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Policy Number:</span>
                  <span className="col-span-2 font-medium">{alert.insuranceData.policyNumber}</span>
                </div>
              )}
              {alert.insuranceData.provider && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Provider:</span>
                  <span className="col-span-2">{alert.insuranceData.provider}</span>
                </div>
              )}
              {alert.insuranceData.expiryDate && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Expiry Date:</span>
                  <span className="col-span-2">{format(parseISO(alert.insuranceData.expiryDate), 'dd MMM yyyy')}</span>
                </div>
              )}
              {alert.insuranceData.daysUntilExpiry !== undefined && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Days Until Expiry:</span>
                  <span className={cn(
                    "col-span-2 font-medium",
                    alert.insuranceData.daysUntilExpiry <= 30 && "text-red-600 dark:text-red-400"
                  )}>
                    {alert.insuranceData.daysUntilExpiry} days
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {alert.warrantyData && (
          <div className="space-y-3 rounded-lg border p-4 bg-muted/50">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Warranty Details
            </h4>
            <div className="grid gap-3 text-sm">
              {alert.warrantyData.provider && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Provider:</span>
                  <span className="col-span-2">{alert.warrantyData.provider}</span>
                </div>
              )}
              {alert.warrantyData.expiryDate && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Expiry Date:</span>
                  <span className="col-span-2">{format(parseISO(alert.warrantyData.expiryDate), 'dd MMM yyyy')}</span>
                </div>
              )}
              {alert.warrantyData.daysUntilExpiry !== undefined && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Days Until Expiry:</span>
                  <span className={cn(
                    "col-span-2 font-medium",
                    alert.warrantyData.daysUntilExpiry <= 30 && "text-red-600 dark:text-red-400"
                  )}>
                    {alert.warrantyData.daysUntilExpiry} days
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {alert.maintenanceData && (
          <div className="space-y-3 rounded-lg border p-4 bg-muted/50">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              Maintenance Details
            </h4>
            <div className="grid gap-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Type:</span>
                <span className="col-span-2 font-medium">{alert.maintenanceData.maintenanceType}</span>
              </div>
              {alert.maintenanceData.lastServiceDate && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Last Service:</span>
                  <span className="col-span-2">{format(parseISO(alert.maintenanceData.lastServiceDate), 'dd MMM yyyy')}</span>
                </div>
              )}
              {alert.maintenanceData.nextServiceDate && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Next Service:</span>
                  <span className="col-span-2">{format(parseISO(alert.maintenanceData.nextServiceDate), 'dd MMM yyyy')}</span>
                </div>
              )}
              {alert.maintenanceData.serviceProvider && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Service Provider:</span>
                  <span className="col-span-2">{alert.maintenanceData.serviceProvider}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Status History */}
        {(alert.acknowledgedDate || alert.resolvedDate) && (
          <div className="space-y-3 rounded-lg border p-4 bg-muted/50">
            <h4 className="text-sm font-semibold">Status History</h4>
            <div className="space-y-2 text-sm">
        {alert.acknowledgedDate && (
          <div>
                  <p className="text-muted-foreground">Acknowledged</p>
                  <p className="font-medium">
              {format(parseISO(alert.acknowledgedDate), 'dd MMM yyyy HH:mm')}
              {alert.acknowledgedBy && ` by ${alert.acknowledgedBy}`}
            </p>
          </div>
        )}

        {alert.resolvedDate && (
          <div>
                  <p className="text-muted-foreground">Resolved</p>
                  <p className="font-medium">
              {format(parseISO(alert.resolvedDate), 'dd MMM yyyy HH:mm')}
              {alert.resolvedBy && ` by ${alert.resolvedBy}`}
            </p>
          </div>
        )}
            </div>
          </div>
        )}

        {/* Notes */}
        {alert.notes && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Notes</h4>
            <p className="text-sm whitespace-pre-wrap rounded-lg border p-3 bg-muted/50">{alert.notes}</p>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}
